import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
/**
 * @jsxRuntime classic
 * @jsx jsx
 */
/** @jsxfrag */

import React, { useCallback, useRef, useState } from 'react';

// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import { css, jsx } from '@emotion/react';
import { useIntl } from 'react-intl-next';
import { getEmptyADF, isEmpty } from '@atlaskit/adf-utils/empty-adf';
import { editorExperiment } from '@atlaskit/tmp-editor-statsig/experiments';
import { useElementBreakpoints } from '../../hooks/useElementBreakpoints';
import { commonTextStyles } from '../../styles/text';
import { AtlassianIntelligenceLogo } from '../AtlassianIntelligenceLogo/AtlassianIntelligenceLogo';
import { BackIconButton } from '../BackIconButton/BackIconButton';
import { CancelIconButton } from '../CancelIconButton/CancelIconButton';
import { PromptEditorWrapper } from '../PromptEditorWrapper/PromptEditorWrapper';
import { PromptMultilineInput } from '../PromptMultilineInput/PromptMultilineInput';
import { SubmitButton } from '../SubmitButton/SubmitButton';
import messages from './messages';
import { buttonWrapperStyles, formContainerStyles, formInnerPaddingTopStyles, formInnerStyles, formPaddingTopStyles, formStyles, logoContainerStyles } from './styles';
var formGapStyles = css({
  gap: "var(--ds-space-050, 4px)"
});

// Make form height equals the height of its parent to prevent the form from growing out of the parent
var formHeightStyles = css({
  height: '100%'
});

// Prevent form inner from growing out of form
var formInnerHeightStyles = css({
  minHeight: '0'
});

// input's line-height is 20px, logo/button's height is 24px, need to adjust the height
var nextToInputStyles = css({
  marginTop: "var(--ds-space-negative-025, -2px)",
  marginBottom: "var(--ds-space-negative-025, -2px)"
});
export var PromptForm = function PromptForm(_ref) {
  var onFormSubmit = _ref.onFormSubmit,
    _onCancel = _ref.onCancel,
    _ref$value = _ref.value,
    value = _ref$value === void 0 ? '' : _ref$value,
    _ref$showButtons = _ref.showButtons,
    showButtons = _ref$showButtons === void 0 ? true : _ref$showButtons,
    tag = _ref.tag,
    _ref$autoFocus = _ref.autoFocus,
    autoFocus = _ref$autoFocus === void 0 ? true : _ref$autoFocus,
    onInputChange = _ref.onInputChange,
    onADFChange = _ref.onADFChange,
    onInputFocus = _ref.onInputFocus,
    onInputBlur = _ref.onInputBlur,
    placeholder = _ref.placeholder,
    ariaLabel = _ref.ariaLabel,
    focusInputRef = _ref.focusInputRef,
    clearInputRef = _ref.clearInputRef,
    _ref$showLogo = _ref.showLogo,
    showLogo = _ref$showLogo === void 0 ? true : _ref$showLogo,
    onInputKeyDown = _ref.onInputKeyDown,
    adfValue = _ref.adfValue,
    enableLinks = _ref.enableLinks,
    PromptEditor = _ref.PromptEditor,
    showBack = _ref.showBack,
    onBack = _ref.onBack,
    onInputKeyUp = _ref.onInputKeyUp,
    fireParentActionButton = _ref.fireParentActionButton,
    focusParentActionButton = _ref.focusParentActionButton,
    refinementTagRef = _ref.refinementTagRef;
  var _useIntl = useIntl(),
    formatMessage = _useIntl.formatMessage;
  var _useElementBreakpoint = useElementBreakpoints(),
    _useElementBreakpoint2 = _slicedToArray(_useElementBreakpoint, 2),
    setBreakpointElement = _useElementBreakpoint2[0],
    breakpoint = _useElementBreakpoint2[1].breakpoint;
  var blurInputRef = useRef(undefined);
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    hasMutipleRows = _useState2[0],
    setHasMutipleRows = _useState2[1];
  var _useState3 = useState(0),
    _useState4 = _slicedToArray(_useState3, 2),
    buttonWidth = _useState4[0],
    setButtonWidth = _useState4[1];
  var _useState5 = useState(false),
    _useState6 = _slicedToArray(_useState5, 2),
    isComposing = _useState6[0],
    setIsComposing = _useState6[1];
  var _useState7 = useState(false),
    _useState8 = _slicedToArray(_useState7, 2),
    isLozengeSelected = _useState8[0],
    setIsLozengeSelected = _useState8[1];
  var lastAdfValue = useRef(getEmptyADF());
  var handleSubmit = function handleSubmit(event) {
    event.preventDefault();
    event.stopPropagation();
    onFormSubmit(value);
  };
  var handleSetButtonWidth = useCallback(function (el) {
    if (el) {
      setButtonWidth(el.getBoundingClientRect().width);
    }
  }, [setButtonWidth]);
  var handleOnCompositionStart = useCallback(function () {
    setIsComposing(true);
  }, []);
  var handleOnCompositionEnd = useCallback(function () {
    setIsComposing(false);
  }, []);
  var promptEditorEnabled = !!PromptEditor;
  var handleKeyUp = React.useCallback(function (event) {
    var isPromptInputEmpty = value === '';
    if (isPromptInputEmpty && promptEditorEnabled && focusParentActionButton) {
      focusParentActionButton(true);
    }
    onInputKeyUp && onInputKeyUp(event);
  }, [focusParentActionButton, onInputKeyUp, promptEditorEnabled, value]);
  var handleKeyDown = React.useCallback(function (event) {
    var isPromptInputEmpty = value === '';

    /**
     * If the key is printable or escape, we should remove the focus ring
     * that is applied to the parent insert or replace button
     */
    if ((event.key.length === 1 || event.key === 'Escape') && focusParentActionButton) {
      focusParentActionButton(false);
    }
    if (editorExperiment('platform_editor_ai_command_palette_post_ga', 'test')) {
      setIsLozengeSelected(false);
    }
    if (promptEditorEnabled) {
      isPromptInputEmpty = Boolean(isEmpty(lastAdfValue === null || lastAdfValue === void 0 ? void 0 : lastAdfValue.current) && adfValue && isEmpty(adfValue));
    }
    if (event.key === 'Backspace' && onBack) {
      if (editorExperiment('platform_editor_ai_command_palette_post_ga', 'test')) {
        // This shortcuts the default functionality by placing focus on the refinement tag
        // instead of removing the tag
        if (!isLozengeSelected && isPromptInputEmpty && refinementTagRef !== null && refinementTagRef !== void 0 && refinementTagRef.current) {
          refinementTagRef.current.getElementsByTagName('button')[0].focus();
          setIsLozengeSelected(true);
          return;
        }
      }
      event.stopPropagation();
      if (isPromptInputEmpty) {
        onBack();
      }
    }
    if (event.key === 'Enter') {
      var _target$getAttribute;
      event.stopPropagation();
      // eslint-disable-next-line @atlaskit/editor/no-as-casting
      var target = event.target;
      if (isComposing) {
        return;
      }

      // If the prompt input is empty, we should fire the parent action button
      // on Enter, which will either be replace or insert
      if (isPromptInputEmpty && fireParentActionButton) {
        fireParentActionButton();
        return;
      }

      // Handles a case where the user presses enter if refinement tag's close button or the
      // cancel button is focused
      if (['close-button-refinement-tag', 'cancel-icon-button', 'cancel-esc-button', 'back-icon-button'].includes((_target$getAttribute = target.getAttribute('data-testid')) !== null && _target$getAttribute !== void 0 ? _target$getAttribute : '')) {
        return;
      }
      // Handles every other case that is not a "shift + enter"
      if (!event.shiftKey) {
        event.preventDefault();
        onFormSubmit(value);
      }
    }

    // Return focus back to the input if blurred, but don't do it if the PM editor is
    // already focused
    if (!['Enter', 'Backspace'].includes(event.key) && !document.getElementsByClassName('ProseMirror-focused').length && editorExperiment('platform_editor_ai_command_palette_post_ga', 'test')) {
      var _document$getElementB;
      (_document$getElementB = document.getElementById('ak-editor-textarea')) === null || _document$getElementB === void 0 || _document$getElementB.focus();
    }
  }, [adfValue, isComposing, fireParentActionButton, focusParentActionButton, onFormSubmit, promptEditorEnabled, onBack, value, isLozengeSelected, refinementTagRef]);
  var onADFChange_ = React.useCallback(function (input) {
    lastAdfValue.current = adfValue ? adfValue : getEmptyADF();
    if (onADFChange) {
      onADFChange(input);
    }
  }, [onADFChange, adfValue]);
  var onInputChange_ = React.useCallback(function (newValue) {
    if (onInputChange) {
      onInputChange(newValue);
    }
  }, [onInputChange]);
  return jsx("div", {
    // ED-19496: We avoid propagating enter keypresses
    // since products like Bitbucket might have parent forms
    // that listen for keydown events to trigger form submission
    onKeyDown: handleKeyDown,
    onKeyUp: handleKeyUp
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/design-system/consistent-css-prop-usage -- Ignored via go/DSP-18766
    ,
    css: formContainerStyles,
    ref: setBreakpointElement
  }, showLogo && jsx("div", {
    css: [
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/design-system/consistent-css-prop-usage -- Ignored via go/DSP-18766
    logoContainerStyles(breakpoint), editorExperiment('platform_editor_ai_command_palate_improvement', 'test') && nextToInputStyles]
  }, jsx(AtlassianIntelligenceLogo, {
    primaryColor: "var(--ds-icon, #44546F)",
    label: formatMessage(messages.logo)
  })), showBack && editorExperiment('platform_editor_ai_command_palate_improvement', 'test') && jsx(BackIconButton, {
    inPromptHeader: true,
    stopPropagation: true,
    onClick: onBack
  }), jsx("form", {
    css: [
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/design-system/consistent-css-prop-usage -- Ignored via go/DSP-18766
    formStyles, editorExperiment('platform_editor_ai_command_palate_improvement', 'test') && formGapStyles, showLogo && !editorExperiment('platform_editor_ai_command_palate_improvement', 'test') &&
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/design-system/consistent-css-prop-usage -- Ignored via go/DSP-18766
    formPaddingTopStyles, editorExperiment('platform_editor_ai_command_palette_post_ga', 'test') && formHeightStyles],
    onSubmit: handleSubmit
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
    ,
    className: "ai-modal-prompt-form",
    "data-testid": "ai-modal-prompt-form"
  }, tag, jsx("div", {
    "data-multiline": hasMutipleRows,
    css: [
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/design-system/consistent-css-prop-usage -- Ignored via go/DSP-18766
    commonTextStyles,
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/design-system/consistent-css-prop-usage -- Ignored via go/DSP-18766
    formInnerStyles, !!tag && !editorExperiment('platform_editor_ai_command_palate_improvement', 'test') &&
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/design-system/consistent-css-prop-usage -- Ignored via go/DSP-18766
    formInnerPaddingTopStyles, editorExperiment('platform_editor_ai_command_palette_post_ga', 'test') && formInnerHeightStyles]
  }, !!PromptEditor && jsx(PromptEditorWrapper, {
    PromptEditor: PromptEditor,
    focusInputRef: focusInputRef,
    blurInputRef: blurInputRef,
    clearInputRef: clearInputRef,
    autoFocus: autoFocus,
    defaultValue: adfValue,
    onInputChange: onInputChange_,
    onADFChange: onADFChange_,
    placeholder: placeholder,
    onFocus: onInputFocus,
    onBlur: onInputBlur,
    onKeyDown: onInputKeyDown,
    onCompositionStart: handleOnCompositionStart,
    onCompositionEnd: handleOnCompositionEnd,
    enableLinks: enableLinks
  }), !PromptEditor && jsx(PromptMultilineInput, {
    focusInputRef: focusInputRef,
    blurInputRef: blurInputRef,
    value: value,
    autoFocus: autoFocus,
    onInputChange: onInputChange_,
    placeholder: placeholder,
    "aria-label": ariaLabel || placeholder,
    onFocus: onInputFocus,
    onBlur: onInputBlur,
    buttonWidth: buttonWidth,
    hasMultipleRows: hasMutipleRows,
    setHasMultipleRows: setHasMutipleRows,
    onKeyDown: onInputKeyDown,
    onCompositionStart: handleOnCompositionStart,
    onCompositionEnd: handleOnCompositionEnd
  }), showButtons && jsx("div", {
    "data-multiline": hasMutipleRows,
    css: [
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/design-system/consistent-css-prop-usage -- Ignored via go/DSP-18766
    buttonWrapperStyles, editorExperiment('platform_editor_ai_command_palate_improvement', 'test') && nextToInputStyles],
    ref: handleSetButtonWidth
  }, jsx(SubmitButton, {
    disabled: !value
  }), jsx(CancelIconButton, {
    onCancel: function onCancel() {
      var _blurInputRef$current;
      _onCancel();
      (_blurInputRef$current = blurInputRef.current) === null || _blurInputRef$current === void 0 || _blurInputRef$current.call(blurInputRef);
    }
  })))));
};